import { useRef } from 'react';
import Layout from '@/components/feature/Layout';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import useEditorialReveal from '@/hooks/useEditorialReveal';
import Img from '@/components/base/Img';
import SectionLabel from '@/components/feature/SectionLabel';
import ProcessSteps from '@/components/feature/ProcessSteps';
import FinalCta from '@/components/feature/FinalCta';
import { services, servicesHeroImage } from '@/mocks/site';

export default function ServicesPage() {
  useDocumentMeta(
    'Services | Exterior Render, Stucco & Mineral Coatings | MATERA',
    'Exterior surfaces only: wall restoration, stucco and render finishes, mineral and lime-based coatings and surface protection, each delivered in three clear steps by MATERA.',
  );
  const scope = useRef<HTMLDivElement>(null);
  useEditorialReveal(scope);
  return (
    <Layout>
      <div ref={scope}>
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-background-50">
        <div className="shell grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-9">
            <SectionLabel index="—">Services</SectionLabel>
            <h1 data-reveal="title" className="display mt-8 text-[clamp(2.4rem,7vw,6.5rem)]">Exterior surfaces, only.</h1>
          </div>
          <p className="lg:col-span-3 self-end text-[15px] leading-relaxed text-foreground-700">
            Four services, one focus: the outer skin of residential buildings. Every job starts by understanding the wall.
          </p>
        </div>
      </section>
      <Img src={servicesHeroImage} alt="Long rendered residential facade in chalk and sandstone tones" className="w-full h-[300px] md:h-[560px]" eager />

      <section className="bg-background-50 py-16 md:py-24">
        <div className="shell flex flex-col gap-20 md:gap-28">
          {services.map((s, i) => (
            <article id={s.id} key={s.id} className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 border-t border-foreground-950 pt-8">
              <div data-reveal="image" className={`lg:col-span-6 overflow-hidden ${i % 2 ? 'lg:order-2' : ''}`}>
                <Img src={s.image} alt={`${s.title} on a residential exterior wall`} className="w-full aspect-[4/3]" />
              </div>
              <div className="lg:col-span-6 flex flex-col">
                <span className="text-[11px] tracking-[0.2em] text-accent-600">{s.number}</span>
                <h2 className="mt-3 font-heading font-bold uppercase tracking-[-0.03em] leading-[0.95] text-3xl md:text-5xl">{s.title}</h2>
                <p className="mt-6 text-[15px] leading-relaxed text-foreground-700 max-w-xl">{s.body}</p>
                <h3 className="mt-8 text-[11px] uppercase tracking-[0.2em] text-foreground-600">Suitable situations</h3>
                <ul className="mt-3 py-1 border-y border-background-300">
                  {s.situations.map((x) => (
                    <li key={x} className="py-2.5 text-sm flex items-center gap-3">
                      <span className="w-1.5 h-1.5 bg-secondary-500" />{x}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-foreground-600">Final recommendation follows an on-site assessment of the substrate.</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-20 md:py-28">
        <div className="shell">
          <SectionLabel index="—">How we work</SectionLabel>
          <h2 data-reveal="title" className="display mt-6 mb-12 text-[clamp(2rem,4.5vw,4rem)]">Every service, three steps</h2>
          <ProcessSteps />
        </div>
      </section>
      <FinalCta />
      </div>
    </Layout>
  );
}